import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { CaretLeft, CaretRight, Plus, Warning, CalendarBlank } from "@phosphor-icons/react";
import AddActivityModal from "../components/crm/AddActivityModal";

const TYPE_COLOR = {
  CALL: "bg-blue-100 text-blue-800 border-blue-200",
  EMAIL: "bg-purple-100 text-purple-800 border-purple-200",
  WHATSAPP: "bg-emerald-100 text-emerald-800 border-emerald-200",
  SAMPLE: "bg-amber-100 text-amber-800 border-amber-200",
  MEETING: "bg-indigo-100 text-indigo-800 border-indigo-200",
  ORDER: "bg-[#E1A104]/20 text-[#5A4108] border-[#E1A104]/40",
  NOTE: "bg-gray-100 text-gray-700 border-gray-200",
};

const pad2 = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

// Month grid spans full weeks (Mon..Sun) covering all days in the month
function buildMonthGrid(anchor) {
  const first = startOfMonth(anchor);
  const last = endOfMonth(anchor);
  const startDow = (first.getDay() + 6) % 7; // Monday = 0
  const start = new Date(first); start.setDate(first.getDate() - startDow);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start); d.setDate(start.getDate() + i);
    cells.push(d);
    if (i >= 27 && d > last && (d.getDay() + 6) % 7 === 6) break;
  }
  // Pad to nearest 7 multiple
  while (cells.length % 7 !== 0) {
    const d = new Date(cells[cells.length - 1]); d.setDate(d.getDate() + 1);
    cells.push(d);
  }
  return cells;
}

export default function Calendar() {
  const { user } = useAuth();
  const [anchor, setAnchor] = useState(startOfMonth(new Date()));
  const [data, setData] = useState({ scheduled: [], unscheduled_open_count: 0, unscheduled_open_preview: [], overdue_open_count: 0 });
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [leadsMap, setLeadsMap] = useState({});
  const [filters, setFilters] = useState({
    agent_id: "All", activity_type: "All", status: "All", priority: "All", search: "",
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);

  const isAdmin = user?.role === "admin";
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

  useEffect(() => {
    if (isAdmin) {
      api.get("/users").then(({ data: users }) => {
        setAgents(users.filter((u) => u.role === "agent"));
      }).catch(() => {});
    }
  }, [isAdmin]);

  const load = async () => {
    setLoading(true);
    const params = {
      date_from: isoDay(startOfMonth(anchor)) + "T00:00:00",
      date_to: isoDay(endOfMonth(anchor)) + "T23:59:59",
    };
    if (isAdmin && filters.agent_id !== "All") params.agent_id = filters.agent_id;
    if (filters.activity_type !== "All") params.activity_type = filters.activity_type;
    if (filters.status !== "All") params.status = filters.status;
    if (filters.priority !== "All") params.priority = filters.priority;
    if (filters.search.trim()) params.search = filters.search.trim();
    try {
      const { data: d } = await api.get("/activities/calendar", { params });
      setData(d);
      // Resolve lead names
      const ids = new Set([...d.scheduled, ...d.unscheduled_open_preview].map((a) => a.lead_id));
      if (ids.size) {
        const { data: leads } = await api.get("/leads", { params: { limit: 500 } });
        const list = Array.isArray(leads) ? leads : leads.leads || [];
        const map = {};
        list.forEach((l) => { if (ids.has(l.id)) map[l.id] = l; });
        setLeadsMap(map);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor, filters.agent_id, filters.activity_type, filters.status, filters.priority]);
  useEffect(() => {
    const t = setTimeout(() => load(), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters.search]);

  const scheduledByDay = useMemo(() => {
    const m = {};
    data.scheduled.forEach((a) => {
      const day = (a.next_follow_up_date || "").slice(0, 10);
      if (!day) return;
      (m[day] ||= []).push(a);
    });
    return m;
  }, [data.scheduled]);

  // UI-STABILITY-1B-tier1: precompute the in-month, sorted day list for the
  // mobile agenda so we don't re-sort/filter the keys on every render.
  const agendaDays = useMemo(() => {
    const monthStart = startOfMonth(anchor);
    const monthEnd = endOfMonth(anchor);
    return Object.keys(scheduledByDay).sort().filter((d) => {
      const date = new Date(d);
      return date >= monthStart && date <= monthEnd;
    });
  }, [scheduledByDay, anchor]);

  const monthLabel = anchor.toLocaleString(undefined, { month: "long", year: "numeric" });
  const cells = useMemo(() => buildMonthGrid(anchor), [anchor]);
  const today = isoDay(new Date());
  const nowIso = new Date().toISOString();

  const openEdit = (act) => { setEditingActivity(act); setModalOpen(true); };
  const openCreate = () => { setEditingActivity(null); setModalOpen(true); };

  const ActivityChip = ({ a, compact = false }) => {
    const lead = leadsMap[a.lead_id];
    const isOverdue = a.status === "Open" && a.next_follow_up_date && a.next_follow_up_date < nowIso;
    const isCompleted = a.status === "Completed";
    const cls = `${TYPE_COLOR[a.activity_type] || "bg-gray-100 border-gray-200"} ${isCompleted ? "opacity-60 line-through" : ""} ${isOverdue ? "ring-2 ring-red-400" : ""}`;
    const label = compact
      ? a.activity_type
      : `${a.activity_type} · ${lead?.company_name || "—"}`;
    return (
      <button
        type="button"
        onClick={() => openEdit(a)}
        data-testid={`calendar-chip-${a.id}`}
        className={`block w-full text-left text-[10px] truncate px-1.5 py-0.5 rounded border ${cls} hover:brightness-95`}
        title={`${a.activity_type} — ${lead?.company_name || ""}${a.title ? ` — ${a.title}` : ""}`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#E1A104] font-semibold">Schedule</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Calendar</h1>
          <p className="text-sm text-gray-500 mt-1">
            Operational view by follow-up date. Activities without a follow-up date appear in the unscheduled card.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="calendar-new-activity"
          className="bg-[#1A3A21] hover:bg-[#142C19] text-white">
          <Plus className="mr-1" /> New activity
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
          className="p-2 rounded-md hover:bg-gray-100" data-testid="calendar-prev"><CaretLeft /></button>
        <div className="font-display text-lg font-semibold min-w-[170px] text-center" data-testid="calendar-month-label">
          {monthLabel}
        </div>
        <button onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
          className="p-2 rounded-md hover:bg-gray-100" data-testid="calendar-next"><CaretRight /></button>
        <Button variant="outline" size="sm" data-testid="calendar-today"
          onClick={() => setAnchor(startOfMonth(new Date()))}>Today</Button>
        <div className="flex-1" />
        {isAdmin && (
          <Select value={filters.agent_id} onValueChange={(v) => setFilters({ ...filters, agent_id: v })}>
            <SelectTrigger className="w-40" data-testid="calendar-agent-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All agents</SelectItem>
              <SelectItem value="_unassigned">Unassigned</SelectItem>
              {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={filters.activity_type} onValueChange={(v) => setFilters({ ...filters, activity_type: v })}>
          <SelectTrigger className="w-36" data-testid="calendar-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All types</SelectItem>
            {Object.keys(TYPE_COLOR).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="w-32" data-testid="calendar-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">Open + Completed</SelectItem>
            <SelectItem value="Open">Open only</SelectItem>
            <SelectItem value="Completed">Completed only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => setFilters({ ...filters, priority: v })}>
          <SelectTrigger className="w-28" data-testid="calendar-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All priority</SelectItem>
            <SelectItem value="A">A</SelectItem><SelectItem value="B">B</SelectItem><SelectItem value="C">C</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Search company/contact…" value={filters.search} className="w-52"
          data-testid="calendar-search"
          onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
      </div>

      {/* Overdue indicator */}
      {data.overdue_open_count > 0 && (
        <div className="border border-red-200 bg-red-50 rounded-md px-3 py-2 flex items-center gap-2 text-sm text-red-700"
          data-testid="calendar-overdue-banner">
          <Warning weight="bold" />
          <span><b>{data.overdue_open_count}</b> overdue open {data.overdue_open_count === 1 ? "activity" : "activities"} need attention.</span>
        </div>
      )}

      {/* Calendar body — Desktop month, Mobile agenda */}
      <div className="hidden md:block">
        <div className="grid grid-cols-7 gap-px bg-gray-200 border border-gray-200 rounded-md overflow-hidden">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="bg-gray-50 text-xs font-semibold uppercase tracking-wider text-gray-500 px-2 py-1.5">{d}</div>
          ))}
          {cells.map((d) => {
            const day = isoDay(d);
            const isCurrentMonth = d.getMonth() === anchor.getMonth();
            const items = scheduledByDay[day] || [];
            const visible = items.slice(0, 3);
            const extra = items.length - visible.length;
            return (
              <div key={day} data-testid={`calendar-day-${day}`}
                className={`bg-white min-h-[110px] p-1.5 flex flex-col gap-1 ${isCurrentMonth ? "" : "bg-gray-50"} ${day === today ? "ring-2 ring-[#E1A104] ring-inset" : ""}`}>
                <div className={`text-[11px] font-semibold ${isCurrentMonth ? "text-gray-700" : "text-gray-400"}`}>{d.getDate()}</div>
                {visible.map((a) => <ActivityChip key={a.id} a={a} compact />)}
                {extra > 0 && <div className="text-[10px] text-gray-500">+{extra} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile agenda */}
      <div className="md:hidden space-y-3" data-testid="calendar-agenda">
        {agendaDays.map((day) => (
          <div key={day} className="bg-white border border-gray-200 rounded-md p-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
              {new Date(day).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
            </div>
            <div className="space-y-1.5">
              {scheduledByDay[day].map((a) => <ActivityChip key={a.id} a={a} />)}
            </div>
          </div>
        ))}
        {Object.keys(scheduledByDay).length === 0 && !loading && (
          <div className="text-sm text-gray-500 text-center py-8 bg-white border border-gray-200 rounded-md">
            <CalendarBlank size={24} className="mx-auto mb-2 text-gray-400" weight="duotone" />
            No scheduled activities this month.
          </div>
        )}
      </div>

      {/* Unscheduled open activities card */}
      <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="calendar-unscheduled-card">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h2 className="font-display text-lg font-semibold">Unscheduled open activities</h2>
          <span className="text-xs text-gray-500" data-testid="unscheduled-count-text">
            <b data-testid="unscheduled-count">{data.unscheduled_open_count}</b> open {data.unscheduled_open_count === 1 ? "activity has" : "activities have"} no follow-up date and are not shown in the calendar grid.
          </span>
        </div>
        {data.unscheduled_open_preview.length > 0 ? (
          <>
            <div className="text-[11px] text-gray-500 mt-1">
              Latest {data.unscheduled_open_preview.length} shown below. Add a follow-up date to make them visible in Calendar.
            </div>
            <div className="mt-3 divide-y divide-gray-100 border border-gray-100 rounded-md">
              {data.unscheduled_open_preview.map((a) => {
                const lead = leadsMap[a.lead_id];
                return (
                  <button key={a.id} type="button" onClick={() => openEdit(a)}
                    data-testid={`unscheduled-preview-${a.id}`}
                    className="w-full text-left flex items-center gap-3 px-3 py-2 hover:bg-gray-50">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${TYPE_COLOR[a.activity_type] || "bg-gray-100 border-gray-200"}`}>
                      {a.activity_type}
                    </span>
                    <span className="text-sm font-medium text-gray-800 truncate flex-1">
                      {lead?.company_name || "—"}
                      {a.title && <span className="text-gray-400 font-normal"> · {a.title}</span>}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}
                    </span>
                    <span className="text-[10px] text-gray-500">{a.priority || ""}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <div className="text-sm text-gray-400 mt-2">No unscheduled open activities in scope.</div>
        )}
      </div>

      <AddActivityModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        editing={editingActivity}
        calendarMode
        onCreated={() => { setModalOpen(false); load(); }}
        onDeleted={() => { setModalOpen(false); load(); }}
      />
    </div>
  );
}
